'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, CheckCircle, ArrowRight, BarChart3, Target } from 'lucide-react';
import { ContactModal } from '@/components/contact-modal';
import { Breadcrumb } from '@/components/breadcrumb';

const services = [
  {
    title: '市场分析',
    description: '深入分析市场趋势、竞争对手和客户需求，为贸易增长提供数据支撑。',
    icon: BarChart3,
    features: ['市场趋势分析', '竞争对手研究', '客户需求洞察'],
  },
  {
    title: '增长策略',
    description: '制定科学的贸易增长策略，优化业务流程，提升运营效率。',
    icon: Target,
    features: ['增长路径规划', '业务流程优化', '运营效率提升'],
  },
  {
    title: '数据驱动',
    description: '建立数据驱动的决策体系，实时监控业务指标，及时调整策略。',
    icon: TrendingUp,
    features: ['数据采集分析', '实时监控预警', '智能决策支持'],
  },
];

const stats = [
  { label: '平均贸易增长', value: '35%', description: '帮助企业实现平均贸易额增长' },
  { label: '服务企业', value: '200+', description: '已服务企业数量' },
  { label: '市场覆盖', value: '15+', description: '覆盖行业领域数量' },
  { label: '客户满意度', value: '98%', description: '客户服务满意度' },
];

const process = [
  { step: 1, title: '现状诊断', description: '全面诊断企业贸易现状和问题' },
  { step: 2, title: '数据分析', description: '深度分析市场数据和业务数据' },
  { step: 3, title: '策略制定', description: '制定科学的增长策略和计划' },
  { step: 4, title: '实施推进', description: '协助企业推进增长策略实施' },
  { step: 5, title: '效果评估', description: '定期评估增长效果并优化' },
];

const partners = [
  '制造业', '电子信息', '新能源', '生物医药', '装备制造', '新材料',
];

export default function TradePage() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '贸易增量' },
  ];

  return (
    <div className="flex flex-col">
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="h-12 w-12 rounded-lg bg-blue-500/30 flex items-center justify-center mb-6">
              <TrendingUp className="h-6 w-6" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight lg:text-5xl">贸易增量服务</h1>
            <p className="mt-6 text-lg text-blue-100">
              通过数据分析与市场策略，帮助企业实现贸易额的稳步增长，
              拓展市场空间，提升核心竞争力。
            </p>
            <Button
              size="lg"
              variant="default"
              className="mt-8 bg-white text-blue-900 hover:bg-gray-100"
              onClick={() => setIsContactOpen(true)}
            >
              立即咨询
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-blue-600">{stat.value}</div>
                <div className="mt-2 text-sm font-medium text-gray-900">{stat.label}</div>
                <div className="mt-1 text-xs text-gray-500">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">服务内容</h2>
            <p className="mt-4 text-lg text-gray-600">全方位的贸易增长解决方案</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.title} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <CardTitle>{service.title}</CardTitle>
                  <CardDescription className="text-gray-600">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-blue-600 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">服务流程</h2>
            <p className="mt-4 text-lg text-gray-600">标准化的服务流程确保效果</p>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item) => (
              <div key={item.step} className="text-center">
                <div className="h-12 w-12 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4 font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">服务领域</h2>
            <p className="mt-4 text-lg text-gray-600">覆盖多个行业领域</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {partners.map((partner) => (
              <div
                key={partner}
                className="px-6 py-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="text-gray-700 font-medium">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold">开启贸易增长之旅</h2>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto">
            立即联系我们，获取专业的贸易增长解决方案
          </p>
          <div className="mt-8">
            <Button
              size="lg"
              variant="default"
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => setIsContactOpen(true)}
            >
              免费咨询
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
}